/**
 * EnsoMirai++ design foundation — the single source of truth.
 *
 * Every token kit in the app (DS2_*, COCKPIT_*, MIRAE_*, V3_*, OV_*, MC_*,
 * KALEIDOSCOPE, the MUI theme) aliases these values instead of declaring its
 * own. Change a value here and every screen follows.
 *
 * Rules of thumb:
 *  - Neutrals (surfaces, text, borders) are CSS variables so they flip with
 *    [data-color-mode]. Use them for any text or background.
 *  - STATUS_HEX / ACCENT_HEX are literal hex so MUI alpha() and canvas/SVG code
 *    can consume them. They are tuned to read on dark surfaces; for *text* on
 *    light surfaces use STATUS_INK (CSS vars with a darker light-mode value).
 *  - This module must stay import-free so any kit can depend on it.
 */

/* ── Fonts ───────────────────────────────────────────────────────────── */

export const FONT = {
  sans: '"IBM Plex Sans", "Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

/* ── Neutrals (theme-aware) ──────────────────────────────────────────── */

export const INK = {
  primary: 'var(--cockpit-ink)',
  secondary: 'var(--cockpit-secondary)',
  tertiary: 'var(--cockpit-tertiary)',
  ghost: 'var(--cockpit-ghost)',
} as const;

export const SURFACE = {
  app: 'var(--app-bg)',
  panel: 'var(--panel-bg)',
  card: 'var(--card-bg)',
  hover: 'var(--hover-bg)',
  selected: 'var(--selected-bg)',
  overlay: 'var(--overlay-bg)',
  /** Always-dark stage behind graphs and diagrams (both modes). */
  stage: 'var(--diagram-stage)',
} as const;

export const BORDER = {
  subtle: 'var(--border-subtle)',
  default: 'var(--border-default)',
  strong: 'var(--border-strong)',
} as const;

export const ELEVATION = {
  sm: 'var(--elevation-sm)',
  md: 'var(--elevation-md)',
  lg: 'var(--elevation-lg)',
} as const;

/* ── Semantic status colors ──────────────────────────────────────────── */

export type StatusTone = 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'active' | 'policy';

/** Literal hex — fills, dots, bars, charts, alpha() tints. */
export const STATUS_HEX = {
  success: '#22D989',
  warning: '#F5B400',
  error: '#F2556A',
  info: '#4D8AF0',
  policy: '#9B7EF0',
  neutral: '#8B93A7',
  active: '#8B9EFF',
} as const;

/** Theme-aware text color for each tone (darker in light mode for contrast). */
export const STATUS_INK: Record<StatusTone, string> = {
  success: 'var(--status-success-ink)',
  warning: 'var(--status-warning-ink)',
  error: 'var(--status-error-ink)',
  info: 'var(--status-info-ink)',
  policy: 'var(--status-policy-ink)',
  neutral: 'var(--cockpit-secondary)',
  active: 'var(--accent-ink)',
};

/** Theme-aware tinted background for each tone. */
export const STATUS_TINT: Record<StatusTone, string> = {
  success: 'var(--status-success-tint)',
  warning: 'var(--status-warning-tint)',
  error: 'var(--status-error-tint)',
  info: 'var(--status-info-tint)',
  policy: 'var(--status-policy-tint)',
  neutral: 'var(--status-neutral-tint)',
  active: 'var(--accent-tint)',
};

/** Lifecycle domains (Discover / Understand / Change / Operate). */
export const DOMAIN_HEX = {
  discover: '#2DD4BF',
  understand: '#9B9EFF',
  change: '#FBBF24',
  operate: '#FB923C',
} as const;

/** Theme-aware domain text colors (darker in light mode). */
export const DOMAIN_INK = {
  discover: 'var(--domain-discover-ink)',
  understand: 'var(--domain-understand-ink)',
  change: 'var(--domain-change-ink)',
  operate: 'var(--domain-operate-ink)',
} as const;

export const ACCENT = {
  main: 'var(--accent)',
  ink: 'var(--accent-ink)',
  tint: 'var(--accent-tint)',
} as const;

/* ── Typography ──────────────────────────────────────────────────────── */

export const TYPE = {
  /** Rare hero moments only (login, empty landing). */
  display: { fontFamily: FONT.sans, fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.2 },
  /** One per screen — the h1 rendered by PageHeader. */
  pageTitle: { fontFamily: FONT.sans, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 },
  /** Panel / section headings. */
  sectionTitle: { fontFamily: FONT.sans, fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
  /** Card headings, list item titles. */
  subheading: { fontFamily: FONT.sans, fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.4 },
  body: { fontFamily: FONT.sans, fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.6 },
  bodySm: { fontFamily: FONT.sans, fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
  caption: { fontFamily: FONT.sans, fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
  /** Field / metric labels — sentence case. */
  label: { fontFamily: FONT.sans, fontSize: '0.8125rem', fontWeight: 500, letterSpacing: '0.01em', lineHeight: 1.35 },
  /** Eyebrow above a title — uppercase, the smallest text allowed. */
  overline: {
    fontFamily: FONT.sans,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1.3,
    textTransform: 'uppercase' as const,
  },
  mono: { fontFamily: FONT.mono, fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
  metric: { fontFamily: FONT.mono, fontSize: '2rem', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 },
  metricSm: { fontFamily: FONT.mono, fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15 },
  prose: { fontFamily: FONT.sans, fontSize: '1rem', fontWeight: 400, lineHeight: 1.75 },
} as const;

/** Smallest legible UI text. Nothing outside canvases/SVG should go below this. */
export const MIN_FONT_SIZE = '0.6875rem';

/* ── Shape, space, motion ────────────────────────────────────────────── */

/** px */
export const RADIUS = {
  chip: 6,
  control: 8,
  card: 12,
  dialog: 16,
  pill: 999,
} as const;

/** MUI spacing units (1 = 8px). */
export const SPACE = {
  xs: 0.5,
  sm: 1.25,
  md: 2,
  lg: 3,
  xl: 4,
  '2xl': 5,
  '3xl': 7,
} as const;

export const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const EASE_TUPLE = [0.22, 1, 0.36, 1] as const;

export const MOTION = {
  fast: `150ms ${EASE}`,
  normal: `220ms ${EASE}`,
  slow: `360ms ${EASE}`,
  easing: EASE_TUPLE,
} as const;

/* ── Control sizing (px) ─────────────────────────────────────────────── */

export const CONTROL = {
  button: { sm: 32, md: 40, lg: 48 },
  icon: { sm: 32, md: 40 },
  chip: { sm: 24, md: 28 },
  tab: 44,
  input: 40,
} as const;

/* ── Page layout ─────────────────────────────────────────────────────── */

export const LAYOUT = {
  /** Readable max width for standard pages; data-dense workspaces opt out. */
  pageMaxWidth: 1440,
  narrowMaxWidth: 960,
  pagePadX: { xs: 2, sm: 3, lg: 4 },
  pagePadTop: { xs: 2, lg: 3 },
  pagePadBottom: { xs: 4, lg: 6 },
  /** Vertical gap between page sections. */
  sectionGap: 3,
  /** Gap between PageHeader and the first section. */
  headerGap: 3,
} as const;
